import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';

import ArrowLeft from '../../../images/arrow-left.svg?react';
import type { ExportChoice } from '../hooks/useRecordExport';

interface RecordsExportDialogProps {
    open: boolean;
    count: number;
    onClose: () => void;
    onChoose: (choice: ExportChoice) => void;
}

/** Asks which report format(s) to download for the selected records. */
function RecordsExportDialog({ open, count, onClose, onChoose }: RecordsExportDialogProps) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Records' });

    const choose = (choice: ExportChoice) => () => {
        // Drop focus from the clicked button first, so closing the dialog doesn't
        // leave focus inside an aria-hidden subtree.
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        onChoose(choice);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth PaperProps={{ sx: { borderRadius: '32px' } }}>
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    borderRadius: '32px',
                    overflow: 'hidden',
                }}
            >
                <div className='flex w-full flex-col items-center gap-4 rounded-[24px] bg-white px-6 py-6'>
                    <div className='text-center text-base font-semibold text-[#535353] leading-tight'>
                        <div className='flex items-center justify-center gap-2 text-sm font-semibold text-[#8B8E9C]'>
                            <span role='img' aria-label='info'>
                                💡
                            </span>
                            <span>{t('Export.description', { count })}</span>
                        </div>
                        <div className='mt-1 text-[#535353] text-sm font-medium'>{t('Export.selectFormat')}:</div>
                    </div>
                    <div className='flex w-full flex-wrap items-center justify-center gap-3'>
                        <Button
                            variant='contained'
                            onClick={choose('csv')}
                            sx={{ minWidth: '120px', borderRadius: '999px', py: 1.5, fontWeight: 700 }}
                        >
                            {t('downloadExcel')}
                        </Button>
                        <Button
                            variant='contained'
                            onClick={choose('pdf')}
                            sx={{ minWidth: '120px', borderRadius: '999px', py: 1.5, fontWeight: 700 }}
                        >
                            {t('downloadPdf')}
                        </Button>
                    </div>
                    <Button
                        variant='outlined'
                        onClick={choose('both')}
                        sx={{
                            width: '65%',
                            borderRadius: '999px',
                            py: 1.5,
                            fontWeight: 700,
                        }}
                    >
                        {t('downloadBoth')}
                    </Button>
                    <Button
                        variant='text'
                        onClick={onClose}
                        startIcon={<ArrowLeft />}
                        sx={{ color: '#4B5563', fontWeight: 600, textTransform: 'none' }}
                    >
                        {t('Export.back')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Shown while reports download; closing it hides it but doesn't cancel the downloads. */
export function ExportPreparingDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Records' });
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogContent className='flex items-center gap-2'>
                <CircularProgress size={20} />
                <span>{t('preparing')}</span>
            </DialogContent>
        </Dialog>
    );
}

export default RecordsExportDialog;
